/**
 * Centralized type definitions for the map domain.
 * This file is the single source of truth for all map-related types
 * following the established Chat domain architecture patterns.
 */

import type { Place, PlaceSearch } from '@generated/proto/model'
import type { SearchPlacesResponse, GetPlaceDetailsResponse } from '@generated/proto/service'
import type { SearchSession } from '@generated/proto/model'
import type { GeoLocation, GeoCornerBounds, LatLngLiteral } from '@/domain/geo'

// Re-export geo types for map domain consumers
export type { GeoLocation, GeoCornerBounds, LatLngLiteral }

/**
 * Core map viewport state representing the current map view.
 * Used throughout the application for map state management.
 */
export interface MapViewport {
  /** Geographic center point of the map */
  center: GeoLocation
  /** Zoom level (0-21, higher = more zoomed in) */
  zoom: number
  /** Visible bounds of the current viewport */
  bounds?: GeoCornerBounds
  /** Map tilt angle in degrees (0-45) */
  pitch?: number
  /** Map rotation in degrees (0-360) */
  heading?: number
}

/**
 * Actions available for managing map viewport state.
 */
export interface MapStateActions {
  /** Set the map center */
  setCenter: (center: GeoLocation) => void
  /** Set the zoom level */
  setZoom: (zoom: number) => void
  /** Set complete viewport state */
  setViewport: (viewport: MapViewport) => void
  /** Fit map to show all markers within bounds */
  fitBounds: (bounds: GeoCornerBounds, padding?: number) => void
  /** Reset to default viewport */
  resetViewport: () => void
}

/**
 * Map marker properties with enhanced features.
 */
export interface MarkerProps {
  /** Unique identifier for the marker */
  id: string
  /** Geographic location */
  location: GeoLocation
  /** Display title */
  title?: string
  /** Short label text */
  label?: string
  /** Icon URL or identifier */
  icon?: string
  /** Whether marker can be dragged */
  draggable?: boolean
  /** Whether marker is visible */
  visible?: boolean
  /** Custom data associated with marker */
  data?: any
  /** Click handler */
  onClick?: (marker: MarkerProps) => void
}

/**
 * Enhanced marker with place data integration.
 */
export interface PlaceSearchMarker extends MarkerProps {
  /** Associated place data from search */
  place: Place
  /** Search metadata for this result */
  searchMetadata?: SearchSession['metadata']
  /** Whether this marker is part of a cluster */
  clustered?: boolean
}

/**
 * Marker cluster representation for performance.
 */
export interface MarkerCluster {
  /** Unique cluster identifier */
  id: string
  /** Cluster center position */
  center: GeoLocation
  /** Number of markers in cluster */
  count: number
  /** Markers in this cluster */
  markers: MarkerProps[]
  /** Cluster bounds */
  bounds: GeoCornerBounds
  /** Zoom level at which cluster breaks apart */
  breakZoom?: number
}

/**
 * Sprite anchor point in pixel coordinates.
 * Defines where on the image to attach to the map position.
 */
export interface SpriteAnchorPoint {
  /** Horizontal pixel offset from image top-left (0 = left edge) */
  u: number
  /** Vertical pixel offset from image top-left (0 = top edge) */
  v: number
}

/**
 * Size constraints for sprite scaling behavior.
 * Sprites scale with map zoom but respect these limits.
 */
export interface SpriteSizeConstraints {
  /** Minimum width in pixels */
  minWidth?: number
  /** Maximum width in pixels */
  maxWidth?: number
  /** Minimum height in pixels */
  minHeight?: number
  /** Maximum height in pixels */
  maxHeight?: number
  /** Base width at reference zoom level (default: natural image width) */
  baseWidth?: number
  /** Base height at reference zoom level (default: natural image height) */
  baseHeight?: number
}

/**
 * Internal sprite state tracking.
 */
export interface SpriteState {
  /** Whether sprite image has loaded */
  loaded: boolean
  /** Whether sprite is currently hovered */
  hovered: boolean
  /** Whether sprite is being dragged */
  dragging: boolean
  /** Current size after scaling calculations */
  currentSize?: { width: number; height: number }
}

/**
 * Map sprite/billboard properties for placing images on the map.
 * Sprites are images placed at geographic coordinates with pixel-precise anchoring.
 */
export interface SpriteProps {
  /** Unique identifier for the sprite */
  id: string
  /** Geographic location on the map */
  location: GeoLocation
  /** Image URL (supports PNG, WebP, or data URLs) */
  imageUrl: string
  /** Anchor point in pixel coordinates on the image */
  anchorPoint: SpriteAnchorPoint
  /** Size constraints for zoom-based scaling */
  sizeConstraints?: SpriteSizeConstraints
  /** Whether sprite can be dragged */
  draggable?: boolean
  /** Whether sprite is visible */
  visible?: boolean
  /** Z-index for layering (higher = on top) */
  zIndex?: number
  /** CSS cursor style when hovering */
  cursor?: string
  /** Custom data associated with sprite */
  data?: any
  /** Click handler */
  onClick?: (sprite: SpriteProps) => void
  /** Hover enter handler */
  onHoverStart?: (sprite: SpriteProps) => void
  /** Hover exit handler */
  onHoverEnd?: (sprite: SpriteProps) => void
  /** Drag start handler */
  onDragStart?: (sprite: SpriteProps) => void
  /** Drag handler with updated location */
  onDrag?: (sprite: SpriteProps, newLocation: GeoLocation) => void
  /** Drag end handler with final location */
  onDragEnd?: (sprite: SpriteProps, newLocation: GeoLocation) => void
  /** Error handler for image load failures */
  onError?: (sprite: SpriteProps, error: Error) => void
}

/**
 * Configuration for animation transitions.
 */
export interface MapAnimationOptions {
  /** Animation duration in milliseconds */
  duration?: number
  /** Easing function type */
  easing?: AnimationEasing
  /** Callback fired when animation completes */
  onComplete?: () => void
  /** Callback fired during animation with progress (0-1) */
  onProgress?: (progress: number) => void
}

/**
 * Available animation easing types.
 */
export type AnimationEasing = 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out' | 'bounce'

/**
 * Return type for map animation hooks.
 */
export interface UseMapAnimationReturn {
  /** Animate to a specific center point */
  animateToCenter: (center: GeoLocation, options?: MapAnimationOptions) => Promise<void>
  /** Animate to a specific zoom level */
  animateToZoom: (zoom: number, options?: MapAnimationOptions) => Promise<void>
  /** Animate to a complete viewport */
  animateToViewport: (viewport: MapViewport, options?: MapAnimationOptions) => Promise<void>
  /** Animate to fit bounds */
  animateToBounds: (bounds: GeoCornerBounds, options?: MapAnimationOptions) => Promise<void>
  /** Whether any animation is currently running */
  isAnimating: boolean
  /** Cancel current animation */
  cancelAnimation: () => void
}

/**
 * Map style theme configuration.
 */
export interface MapStyleTheme {
  /** Unique theme identifier */
  id: string
  /** Human-readable theme name */
  name: string
  /** Google Maps style configuration */
  styles: google.maps.MapTypeStyle[]
  /** Optional preview image URL */
  preview?: string
  /** Whether this is a dark theme */
  isDark?: boolean
}

/**
 * Map styling configuration and actions.
 */
export interface MapStylingConfig {
  /** Currently active theme */
  currentTheme: MapStyleTheme
  /** Available themes */
  availableThemes: MapStyleTheme[]
  /** Custom style overrides */
  customStyles?: google.maps.MapTypeStyle[]
  /** Actions for theme management */
  actions: {
    setTheme: (theme: MapStyleTheme) => void
    updateCustomStyles: (styles: google.maps.MapTypeStyle[]) => void
    resetStyles: () => void
  }
}

/**
 * Geolocation service state and capabilities.
 */
export interface GeolocationState {
  /** Current user location */
  currentLocation: GeoLocation | null
  /** Permission status */
  permissionStatus: PermissionStatus | null
  /** Whether location services are available */
  isAvailable: boolean
  /** Location accuracy in meters */
  accuracy?: number
  /** Timestamp of last location update */
  timestamp?: Date
}

/**
 * Geolocation service actions.
 */
export interface GeolocationActions {
  /** Request current location once */
  getCurrentLocation: (options?: GeolocationOptions) => Promise<GeoLocation>
  /** Start watching location changes */
  watchLocation: (callback: (location: GeoLocation) => void, options?: GeolocationOptions) => () => void
  /** Request location permission */
  requestPermission: () => Promise<PermissionStatus>
  /** Stop watching location */
  stopWatching: () => void
}

/**
 * Configuration for geolocation requests.
 */
export interface GeolocationOptions {
  /** Maximum age of cached position in milliseconds */
  maximumAge?: number
  /** Timeout for position request in milliseconds */
  timeout?: number
  /** Whether to use high accuracy GPS */
  enableHighAccuracy?: boolean
}

/**
 * Place search integration state.
 */
export interface PlaceSearchState {
  /** Current search query */
  query: string
  /** Search results */
  results: Place[]
  /** Search loading state */
  loading: boolean
  /** Search error */
  error: Error | null
  /** Search metadata */
  metadata?: SearchSession['metadata']
  /** Associated markers for results */
  resultMarkers: PlaceSearchMarker[]
}

/**
 * Place search actions.
 */
export interface PlaceSearchActions {
  /** Perform place search */
  searchPlaces: (search: PlaceSearch) => Promise<SearchPlacesResponse>
  /** Get detailed place information */
  getPlaceDetails: (placeId: string) => Promise<GetPlaceDetailsResponse>
  /** Search places near current viewport */
  searchNearViewport: (query: string, viewport: MapViewport) => Promise<Place[]>
  /** Get places within bounds */
  getPlacesInBounds: (bounds: GeoCornerBounds) => Promise<Place[]>
  /** Clear search results */
  clearResults: () => void
}

/**
 * Map error types with recovery strategies.
 */
export class MapError extends Error {
  constructor(
    message: string,
    public code: MapErrorCode,
    public context?: Record<string, any>,
    public recoverable?: boolean,
    public retryable?: boolean
  ) {
    super(message)
    this.name = 'MapError'
    
    // Set default recovery strategies based on error type
    if (recoverable === undefined) {
      this.recoverable = this.isRecoverableByDefault()
    }
    if (retryable === undefined) {
      this.retryable = this.isRetryableByDefault()
    }
  }

  private isRecoverableByDefault(): boolean {
    switch (this.code) {
      case MapErrorCode.GEOLOCATION_ERROR:
      case MapErrorCode.PLACE_SEARCH_ERROR:
      case MapErrorCode.NETWORK_ERROR:
        return true
      case MapErrorCode.GOOGLE_MAPS_API_ERROR:
        return true
      case MapErrorCode.PERMISSION_DENIED:
      case MapErrorCode.INVALID_CONFIGURATION:
        return false
      default:
        return false
    }
  }

  private isRetryableByDefault(): boolean {
    switch (this.code) {
      case MapErrorCode.NETWORK_ERROR:
      case MapErrorCode.PLACE_SEARCH_ERROR:
        return true
      case MapErrorCode.GEOLOCATION_ERROR:
      case MapErrorCode.GOOGLE_MAPS_API_ERROR:
        return true
      case MapErrorCode.PERMISSION_DENIED:
      case MapErrorCode.INVALID_CONFIGURATION:
        return false
      default:
        return false
    }
  }

  getRecoveryMessage(): string {
    if (!this.recoverable) {
      return 'This error cannot be automatically recovered from.'
    }

    switch (this.code) {
      case MapErrorCode.GEOLOCATION_ERROR:
        return 'Unable to get your location. Please check location permissions.'
      case MapErrorCode.PLACE_SEARCH_ERROR:
        return 'Place search failed. Please try a different search term.'
      case MapErrorCode.NETWORK_ERROR:
        return 'Network connection issue. Please check your internet connection.'
      case MapErrorCode.GOOGLE_MAPS_API_ERROR:
        return 'Map service temporarily unavailable. Please try again.'
      case MapErrorCode.PERMISSION_DENIED:
        return 'Location permission denied. Please enable location access in settings.'
      default:
        return 'Please try again or contact support if the problem persists.'
    }
  }

  getRetryDelay(): number {
    if (!this.retryable) return 0

    switch (this.code) {
      case MapErrorCode.NETWORK_ERROR:
        return 2000 // 2 seconds
      case MapErrorCode.PLACE_SEARCH_ERROR:
        return 1000 // 1 second
      case MapErrorCode.GEOLOCATION_ERROR:
        return 3000 // 3 seconds
      case MapErrorCode.GOOGLE_MAPS_API_ERROR:
        return 5000 // 5 seconds
      default:
        return 2000
    }
  }
}

/**
 * Map error codes.
 */
export enum MapErrorCode {
  GEOLOCATION_ERROR = 'GEOLOCATION_ERROR',
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  GOOGLE_MAPS_API_ERROR = 'GOOGLE_MAPS_API_ERROR',
  PLACE_SEARCH_ERROR = 'PLACE_SEARCH_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  INVALID_CONFIGURATION = 'INVALID_CONFIGURATION',
  ANIMATION_ERROR = 'ANIMATION_ERROR',
  CLUSTERING_ERROR = 'CLUSTERING_ERROR'
}

/**
 * Configuration for map behavior.
 */
export interface MapConfig {
  /** Default viewport settings */
  defaultViewport: MapViewport
  /** Google Maps API key */
  apiKey: string
  /** Maximum number of markers before clustering */
  clusterThreshold?: number
  /** Animation duration defaults */
  animationDuration?: number
  /** Whether to persist viewport state */
  persistViewport?: boolean
  /** Geolocation options */
  geolocation?: GeolocationOptions
}

/**
 * Map state management return type.
 */
export interface UseMapStateReturn {
  /** Current viewport state */
  viewport: MapViewport
  /** Viewport management actions */
  actions: MapStateActions
  /** Loading state */
  loading: boolean
  /** Error state */
  error: MapError | null
  /** Whether map is ready for interaction */
  isReady: boolean
}

/**
 * Combined map functionality return type.
 */
export interface UseMapReturn extends UseMapStateReturn {
  /** Animation capabilities */
  animations: UseMapAnimationReturn
  /** Styling configuration */
  styling: MapStylingConfig
  /** Geolocation state and actions */
  geolocation: {
    state: GeolocationState
    actions: GeolocationActions
  }
  /** Place search integration */
  placeSearch: {
    state: PlaceSearchState
    actions: PlaceSearchActions
  }
  /** Marker management */
  markers: {
    all: MarkerProps[]
    clusters: MarkerCluster[]
    actions: {
      addMarker: (marker: MarkerProps) => void
      removeMarker: (markerId: string) => void
      updateMarker: (markerId: string, updates: Partial<MarkerProps>) => void
      clearMarkers: () => void
    }
  }
  /** Sprite management */
  sprites: {
    all: SpriteProps[]
    actions: {
      addSprite: (sprite: SpriteProps) => void
      removeSprite: (spriteId: string) => void
      updateSprite: (spriteId: string, updates: Partial<SpriteProps>) => void
      clearSprites: () => void
    }
  }
  /** Viewport adaptation to Shoji panel changes */
  viewportAdaptation: {
    /** Current available map space */
    availableSpace: {
      width: number
      height: number
      offsetX: number
      offsetY: number
    }
    /** Whether viewport is currently adapting */
    isAdapting: boolean
    /** Manually trigger viewport adaptation */
    adaptToCurrentSpace: () => Promise<void>
    /** Get current space without triggering adaptation */
    getCurrentSpace: () => {
      width: number
      height: number
      offsetX: number
      offsetY: number
    }
  }
}